'use client'

import { useEffect, useRef, useState } from 'react'

import { useCategoriesList } from '@/features/categories/hooks/useCategoriesList'
import type { Category } from '@/features/categories/types'
import type { OutlineGeometry } from '../types/categories.types'

const INITIAL_GEOMETRY: OutlineGeometry = {
  width: 0,
  activeLeft: null,
  activeWidth: null,
}

const useOutlineGeometry = (
  sectionRef: React.RefObject<HTMLDivElement | null>,
  tabRefs: React.MutableRefObject<Array<HTMLButtonElement | null>>,
  selectedIndex: number,
  loading: boolean,
  totalTabs: number,
  enabled: boolean,
) => {
  const [geometry, setGeometry] = useState<OutlineGeometry>(INITIAL_GEOMETRY)
  const frameRef = useRef<number | null>(null)
  const timeoutRef = useRef<number | null>(null)

  useEffect(() => {
    if (loading || !enabled) {
      return
    }

    function measure() {
      frameRef.current = null
      timeoutRef.current = null

      const section = sectionRef.current
      if (!section) return
      const sectionWidth = section.clientWidth
      const activeTab = tabRefs.current[selectedIndex]
      const nextGeometry = activeTab
        ? {
            width: sectionWidth,
            activeLeft: activeTab.offsetLeft,
            activeWidth: activeTab.offsetWidth,
          }
        : {
            width: sectionWidth,
            activeLeft: null,
            activeWidth: null,
          }

      setGeometry((previous) =>
        previous.width === nextGeometry.width &&
        previous.activeLeft === nextGeometry.activeLeft &&
        previous.activeWidth === nextGeometry.activeWidth
          ? previous
          : nextGeometry,
      )
    }

    function scheduleMeasure() {
      if (frameRef.current !== null || timeoutRef.current !== null) return

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        timeoutRef.current = window.setTimeout(measure, 0)
      })
    }

    scheduleMeasure()
    const observer = new ResizeObserver(scheduleMeasure)
    const section = sectionRef.current
    if (section) observer.observe(section)
    const activeTab = tabRefs.current[selectedIndex]
    if (activeTab) observer.observe(activeTab)
    return () => {
      observer.disconnect()
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [sectionRef, tabRefs, selectedIndex, totalTabs, loading, enabled])

  return geometry
}

const useActiveCategoriesBreakpoint = () => {
  const [activeBreakpoint, setActiveBreakpoint] = useState<
    'mobile' | 'desktop' | null
  >(null)

  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)')
    const updateBreakpoint = () => {
      setActiveBreakpoint(query.matches ? 'desktop' : 'mobile')
    }

    updateBreakpoint()
    query.addEventListener('change', updateBreakpoint)
    return () => query.removeEventListener('change', updateBreakpoint)
  }, [])

  return activeBreakpoint
}

export const useCategoriesSection = () => {
  const { categories, loading } = useCategoriesList()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const activeBreakpoint = useActiveCategoriesBreakpoint()

  const desktopSectionRef = useRef<HTMLDivElement>(null)
  const desktopTabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const mobileSectionRef = useRef<HTMLDivElement>(null)
  const mobileTabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const desktopGeometry = useOutlineGeometry(
    desktopSectionRef,
    desktopTabRefs,
    selectedIndex,
    loading,
    categories.length,
    activeBreakpoint === 'desktop',
  )

  const mobileGeometry = useOutlineGeometry(
    mobileSectionRef,
    mobileTabRefs,
    selectedIndex,
    loading,
    categories.length,
    activeBreakpoint === 'mobile',
  )

  useEffect(() => {
    if (categories.length === 0) {
      return
    }
    setSelectedIndex((prev) => Math.min(prev, categories.length - 1))
  }, [categories.length])

  const activeCategory: Category | undefined = categories[selectedIndex]
  const subcategories = activeCategory?.subcategories ?? []

  return {
    categories,
    loading,
    selectedIndex,
    setSelectedIndex,
    subcategories,
    desktopSectionRef,
    desktopTabRefs,
    desktopGeometry,
    mobileSectionRef,
    mobileTabRefs,
    mobileGeometry,
  }
}
